import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRouteHelpers } from "routes/helpers";
import routes from "routes/routes";
import actions from "store/actions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { companyMetadataSelector, userMetadataSelector } from "store/selectors";

import { AppEvents } from "services/events";
import { COMPANIES_LIST_FETCH_ALL_LIMIT } from "constants/company/companiesList";
import { showErrorModal } from "helpers";
import useAuth from "hooks/useAuth";
import { useCompaniesList } from "hooks/useCompaniesList";
import { ISignUpPageLocationState } from "pages/Auth/SignUpPage/types";

import {
  mutationUsersControllerPinCompany,
  mutationUsersControllerUnpinCompany,
} from "utils/swagger_react_query";

export const useCompanySwitcherDropdown = () => {
  const navigate = useNavigate();
  const currentUser = useAppSelector(userMetadataSelector);
  const currentCompany = useAppSelector(companyMetadataSelector);

  const dispatch = useAppDispatch();
  const { getDefaultRoute } = useRouteHelpers();
  const [isOpen, setIsOpen] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);
  const { selectActiveCompany, getCurrentUserAndCompany } = useAuth();
  const {
    companiesList,
    searchInputValue,
    actions: companiesListActions,
  } = useCompaniesList({
    setIsListLoading,
    initValueFromStore: true,
    perPage: COMPANIES_LIST_FETCH_ALL_LIMIT,
  });

  const handleDropdownClick = (state?: boolean) => {
    setIsOpen(state ?? !isOpen);

    if (isOpen || state === false) {
      companiesListActions.resetData(true);
    }
  };

  const handleCompanyClick = async (companyId: string) => {
    try {
      const shouldRefetchList = companiesList.companies.find(
        (it) => it.companyId === companyId && !it.hasSignedIn,
      );
      setIsListLoading(true);
      await selectActiveCompany(companyId);

      dispatch(actions.userMetaData.setGlobalLoading(true));
      handleDropdownClick(false);
      if (shouldRefetchList) await companiesListActions.refetchCompanies(undefined, true);
      const userAndCompanyResult = await getCurrentUserAndCompany();
      const user = userAndCompanyResult?.user;
      const company = userAndCompanyResult?.company;

      AppEvents.emit("RoleHasUpdated", {
        roleHasUpdated: false,
        userData: user,
      });
      navigate(getDefaultRoute(user, company));
    } catch (error) {
      showErrorModal(error);
    } finally {
      dispatch(actions.userMetaData.setGlobalLoading(false));
      setIsListLoading(false);
      setTimeout(() => AppEvents.emit("RoleHasUpdated", { roleHasUpdated: false }));
    }
  };

  const handleCreateNewCompanyClick = () => {
    const locationState: ISignUpPageLocationState = {
      isAdditionalCompanyCreation: true,
    };
    navigate(routes.SIGN_UP_COMPLETE, { state: locationState });
  };

  const handlePinCompanyClick = async (companyId: string, currentState: boolean) => {
    try {
      setIsListLoading(true);
      if (currentState) {
        await mutationUsersControllerUnpinCompany({ companyId })();
      } else {
        await mutationUsersControllerPinCompany()({ companyId });
      }
      companiesListActions.resetData();
      await companiesListActions.refetchCompanies(undefined, true);
    } catch (error) {
      showErrorModal(error);
    } finally {
      setIsListLoading(false);
    }
  };

  return {
    metadata: {
      currentUser,
      currentCompany,
      isListLoading,
      isOpen,
    },
    data: {
      companiesList,
      searchInputValue,
    },
    actions: {
      handleDropdownClick,
      handleCompanyClick,
      handleCreateNewCompanyClick,
      handleSearchInputChange: companiesListActions.handleSearchInputChange,
      handlePinCompanyClick,
    },
  };
};
